/// <amd-module name="Feed/Legacy/Components/PackageUpstreamSource" />
import * as React from "react";

import { findIndex } from "OfficeFabric/Utilities";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

import { Link } from "VSSUI/Link";
import { TooltipHost, TooltipOverflowMode } from "VSSUI/Tooltip";

import { MinimalPackageVersion, Package, UpstreamSource, UpstreamSourceType } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { getPackageDetailsPageUrl } from "Feed/Legacy/Helpers/UrlHelper";
import { MavenKey } from "Feed/Legacy/Protocols/Maven/Constants/MavenConstants";
import { NpmKey } from "Feed/Legacy/Protocols/Npm/Constants/NpmConstants";
import { NuGetKey } from "Feed/Legacy/Protocols/NuGet/Constants/NuGetConstants";
import { PyPiKey } from "Feed/Legacy/Protocols/PyPi/PyPiConstants";
import { UPackKey } from "Feed/Legacy/Protocols/UPack/UPackConstants";

import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IPackageUpstreamSourceProps {
    pageContext: IVssPageContext;
    packageSummary: Package;
    packageVersion: MinimalPackageVersion;
    upstreamSources: UpstreamSource[];
}

export class PackageUpstreamSource extends React.PureComponent<IPackageUpstreamSourceProps> {
    public render(): JSX.Element {
        return this._getUpstreamSourceElement();
    }

    private _getUpstreamSourceElement(): JSX.Element {
        const localSourceName = this._packageToLocalUpstreamSourceName();
        if (localSourceName != null) {
            return (
                <span
                    className="upstream-source-grid-cell"
                    aria-label={PackageResources.AriaLabel_PackageListItem_Upstream}
                >
                    {localSourceName}
                </span>
            );
        }

        const upstreamSource = this._packageToUpstreamSource();
        if (upstreamSource == null) {
            return (
                <span
                    className="upstream-source-grid-cell"
                    aria-label={PackageResources.AriaLabel_PackageListItem_Upstream}
                >
                    {PackageResources.FeedSources_Unknown}
                </span>
            );
        }

        if (upstreamSource.upstreamSourceType === UpstreamSourceType.Public) {
            return (
                <span
                    className="upstream-source-grid-cell"
                    aria-label={PackageResources.AriaLabel_PackageListItem_Upstream}
                >
                    {upstreamSource.name}
                </span>
            );
        }

        // Temporary while strategy to communicate with other accounts is figured out
        const pageData: IPageData = this.props.pageContext.getService<IVssPageService>("IVssPageService").getData();
        const collectionContext: { id: string } = { id: pageData.hostId };

        if (Utils_String.equals(collectionContext.id, upstreamSource.internalUpstreamCollectionId, true) === false) {
            return (
                <span
                    className="upstream-source-grid-cell"
                    aria-label={PackageResources.AriaLabel_PackageListItem_Upstream}
                >
                    {upstreamSource.name}
                </span>
            );
        }

        const packageDetailsPageUrl = getPackageDetailsPageUrl(
            this.props.pageContext,
            upstreamSource.internalUpstreamFeedId,
            this.props.packageSummary,
            this.props.packageVersion.version
        );
        return (
            <TooltipHost content={upstreamSource.name} overflowMode={TooltipOverflowMode.Parent}>
                <Link href={packageDetailsPageUrl} target={"_blank"}>
                    {upstreamSource.name}
                </Link>
            </TooltipHost>
        );
    }

    private _packageToLocalUpstreamSourceName(): string {
        if (
            this._isUpstreamSourceDisplayEnabledForProtocol(this.props.packageSummary.protocolType) === false ||
            this.props.packageVersion.directUpstreamSourceId == null
        ) {
            return PackageResources.FeedSources_Local;
        }

        return null;
    }

    private _packageToUpstreamSource(): UpstreamSource {
        if (this._isUpstreamSourceDisplayEnabledForProtocol(this.props.packageSummary.protocolType) === false) {
            return null;
        }

        let index: number = -1;
        if (this.props.packageVersion.directUpstreamSourceId == null) {
            return null;
        }

        if (this.props.upstreamSources == null || this.props.upstreamSources.length === 0) {
            return null;
        }

        index = findIndex(this.props.upstreamSources, (upstreamSource: UpstreamSource) => {
            return Utils_String.equals(upstreamSource.id, this.props.packageVersion.directUpstreamSourceId, true);
        });

        if (index > -1) {
            return this.props.upstreamSources[index];
        }

        return null;
    }

    private _isUpstreamSourceDisplayEnabledForProtocol(protocolType: string): boolean {
        const isUpackSameCollectionUpstreamAvailable = isFeatureFlagEnabled(this.props.pageContext,
                                                        "Packaging.Feed.UPackSameCollectionUpstreams", false);

        const isUpackCrossCollectionUpstreamAvailable = isFeatureFlagEnabled(this.props.pageContext,
                                                        "Packaging.Feed.UPackCrossCollectionUpstreams", false);                                               

        return (
            ((isUpackSameCollectionUpstreamAvailable || isUpackCrossCollectionUpstreamAvailable) && 
                Utils_String.equals(protocolType, UPackKey, true) === true) ||
            Utils_String.equals(protocolType, NuGetKey, true) === true ||
            Utils_String.equals(protocolType, NpmKey, true) === true ||
            Utils_String.equals(protocolType, PyPiKey, true) === true ||
            Utils_String.equals(protocolType, MavenKey, true) === true
        );
    }
}
